import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import KeyOutlinedIcon from '@mui/icons-material/KeyOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import { showInfoToast } from 'lib/ToastHelper';
import React, { useMemo } from 'react';

import './BPAFindingPermissions.css';

interface BPAFindingExternalAccessPermissionsProps {
  permissions: Record<string, unknown>;
}

type PolicyStatement = Record<string, unknown>;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value && typeof value === 'object' && !Array.isArray(value));

const normalizeStatements = (value: unknown): PolicyStatement[] => {
  if (Array.isArray(value)) return value.filter(isRecord);
  return isRecord(value) ? [value] : [];
};

const formatPolicyValue = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return '-';
  if (Array.isArray(value)) {
    return value.map(formatPolicyValue).join(', ');
  }
  if (isRecord(value)) return JSON.stringify(value);
  return String(value);
};

const renderPolicyActions = (value: unknown): React.ReactNode => {
  const actions = Array.isArray(value) ? value : [value];
  const displayActions = actions.length > 0 ? actions : [null];

  return (
    <div className="bpa-policy-action-list">
      {displayActions.map((action, index) => (
        <span key={`${formatPolicyValue(action)}-${index}`}>
          {formatPolicyValue(action)}
        </span>
      ))}
    </div>
  );
};

const getPrincipal = (statement: PolicyStatement): string => {
  const principal = statement.Principal;
  if (!isRecord(principal)) return formatPolicyValue(principal);
  return formatPolicyValue(principal.AWS);
};

const getExternalId = (statement: PolicyStatement): string => {
  const condition = statement.Condition;
  if (!isRecord(condition)) return '-';

  const stringEquals = condition.StringEquals;
  if (!isRecord(stringEquals)) return '-';

  return formatPolicyValue(stringEquals['sts:ExternalId']);
};

const renderJsonLine = (line: string): React.ReactNode[] => {
  const tokenPattern =
    /"(?:\\.|[^"\\])*"|\b(?:true|false|null)\b|-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?/g;
  const parts: React.ReactNode[] = [];
  let cursor = 0;
  let match = tokenPattern.exec(line);

  while (match) {
    const token = match[0];
    if (match.index > cursor) {
      parts.push(line.slice(cursor, match.index));
    }

    const remainingLine = line.slice(match.index + token.length);
    let tokenClass = 'bpa-policy-json-number';
    if (token.startsWith('"')) {
      tokenClass = /^\s*:/.test(remainingLine)
        ? 'bpa-policy-json-key'
        : 'bpa-policy-json-string';
    } else if (token === 'true' || token === 'false') {
      tokenClass = 'bpa-policy-json-boolean';
    } else if (token === 'null') {
      tokenClass = 'bpa-policy-json-null';
    }

    parts.push(
      <span className={tokenClass} key={`${match.index}-${token}`}>
        {token}
      </span>,
    );
    cursor = match.index + token.length;
    match = tokenPattern.exec(line);
  }

  if (cursor < line.length) parts.push(line.slice(cursor));
  return parts;
};

const BPAFindingExternalAccessPermissions: React.FC<
  BPAFindingExternalAccessPermissionsProps
> = ({ permissions }) => {
  const statements = useMemo(
    () => normalizeStatements(permissions.Statement),
    [permissions],
  );
  const policyJson = useMemo(
    () => JSON.stringify(permissions, null, 2),
    [permissions],
  );
  const policyLines = useMemo(() => policyJson.split('\n'), [policyJson]);

  const copyPolicy = () => {
    void navigator.clipboard.writeText(policyJson);
    showInfoToast('Policy copied to clipboard');
  };

  return (
    <section
      className="bpa-external-access-permissions"
      aria-label="External access policy details"
    >
      <header className="bpa-policy-heading">
        <h3>Policy Structure</h3>
        <p>Raw policy structure for this role&apos;s trust policy.</p>
      </header>

      {statements.map((statement, index) => (
        <div className="bpa-policy-summary" key={`statement-${index}`}>
          {statements.length > 1 && (
            <div className="bpa-policy-statement-label">
              Statement {index + 1}
            </div>
          )}
          <div className="bpa-policy-summary-grid">
            <div className="bpa-policy-summary-item">
              <CategoryOutlinedIcon
                className="bpa-policy-icon-action"
                aria-hidden="true"
              />
              <div>
                <strong>Action</strong>
                {renderPolicyActions(statement.Action)}
              </div>
            </div>
            <div className="bpa-policy-summary-item">
              <VerifiedUserOutlinedIcon
                className="bpa-policy-icon-effect"
                aria-hidden="true"
              />
              <div>
                <strong>Effect</strong>
                <span>{formatPolicyValue(statement.Effect)}</span>
              </div>
            </div>
            <div className="bpa-policy-summary-item">
              <PersonOutlineIcon
                className="bpa-policy-icon-principal"
                aria-hidden="true"
              />
              <div>
                <strong>Principal</strong>
                <span title={getPrincipal(statement)}>
                  {getPrincipal(statement)}
                </span>
              </div>
            </div>
            <div className="bpa-policy-summary-item">
              <KeyOutlinedIcon
                className="bpa-policy-icon-external-id"
                aria-hidden="true"
              />
              <div>
                <strong>External ID</strong>
                <span title={getExternalId(statement)}>
                  {getExternalId(statement)}
                </span>
              </div>
            </div>
          </div>
        </div>
      ))}

      <div className="bpa-policy-code-panel">
        <header>
          <strong>policy.json</strong>
          <button type="button" onClick={copyPolicy}>
            <ContentCopyOutlinedIcon aria-hidden="true" />
            <span>Copy</span>
          </button>
        </header>
        <div className="bpa-policy-code" role="region" aria-label="Policy JSON">
          {policyLines.map((line, index) => (
            <div className="bpa-policy-code-line" key={`line-${index}`}>
              <span className="bpa-policy-line-number">{index + 1}</span>
              <code>{renderJsonLine(line)}</code>
            </div>
          ))}
        </div>
      </div>

      <div className="bpa-policy-note">
        <ShieldOutlinedIcon aria-hidden="true" />
        <span>
          This view preserves the raw policy semantics. Validation and
          interpretation are performed by AWS.
        </span>
      </div>
    </section>
  );
};

export default BPAFindingExternalAccessPermissions;
